"use client";
import React from "react";
import { Suspense } from "react";
import styles from "../getting-the-basis-of-advice-right-page/getting-the-basis-of-advice-right-page.module.css";
import "../../../src/common/grid.css";
import BlogNavigation from "../../../components/BlogNavigation/BlogNavigation";
import BlogSearchSection from "../../../components/BlogSearchSection/BlogSearchSection";
import Disclaimer from "../../../components/BlogDiclaimerComponent/BlogDisclaimerComponent";
import Link from "next/link";
import BlogCardSlider from "../../../components/BlogCardSlider/BlogCardSlider";
import { useTranslation } from "react-i18next";
import BlogSpreadLoveComponent from "../../../components/BlogSpreadLoveComponent/BlogSpreadLoveComponent";
import BlogStartSection from "../../../components/BlogStartSection/BlogStartSection";
import BlogLeaveReplyComponent from "../../../components/BlogLeaveReplyComponent/BlogLeaveReplyComponent";
import { usePosts } from "../../../src/app/blogs/posts.js";
import understandingPropertySecurity from "../../../src/assets/images/understandingPropertySecurity.webp";


export default function UnderstandingPropertySecurity() {
    const { t } = useTranslation();
    const posts = usePosts();
    // Blog metadata
    const blogData = {
        image: understandingPropertySecurity,
        category: t("New Blogs"),
        title: t("Understanding Property Security"),
        url: "understanding-property-security",
        author: "Matthew Clark",
        categorySlug: "new-blogs",
        relatedCategory: "new-blogs",
        displayCategory: t("New Blogs"),
    };

    const relatedPosts = posts
        .filter((post) => post.relatedCategory === blogData.relatedCategory && post.slug !== blogData.url)
        .slice(0, 10);

    return (
        <>
            <div className={styles.blogWrapper}>
                <div className={`${styles.flexContainer} container`}>
                    <div className="row">
                        <div className="col-12 col-md-8 col-lg-8">
                            <div className={styles.blogContentWrapper}>
                                <BlogNavigation
                                    items={[
                                        { title: t("Key Choice Lending"), path: "/" },
                                        { title: t("Blogs"), path: "/blogs" },
                                        {
                                            title: t("New Blogs"),
                                            path: `/blogs/category/${blogData.categorySlug}`,
                                        },
                                    ]}
                                    currentPageTitle={blogData.title}
                                />

                                <BlogStartSection
                                    title={blogData.title}
                                    author={blogData.author}
                                    category={blogData.relatedCategory}
                                    image={blogData.image}
                                    alt={t("understanding-property-security-image")}
                                />

                                <BlogSpreadLoveComponent url={blogData.url} title={blogData.title} />

                                <article className={styles.blogContent}>
                                    <h1 className={styles.big}>
                                        {t("Understanding Property Security: Safeguarding Loans")}
                                    </h1>
                                    <p className={styles.intro}>
                                        {t(
                                            "Are you looking to secure a loan for your business or personal needs? In the world of finance, property security plays a crucial role in safeguarding lenders against potential losses while also providing borrowers with access to more favourable loan terms. If you’ve ever wondered how lenders protect their interests and borrowers gain an advantage, then you’ve come to the right place. In this article, we delve into the concept of property security and its importance in the lending landscape."
                                        )}
                                    </p>
                                    <section className={styles.contentSection}>
                                        <h1 className={styles.big}>{t("Property Security")}</h1>
                                        <p className={styles.small}>
                                            {t(
                                                "Security refers to the assets provided by a borrower to a lender as a safeguard for a loan. It acts as a protection for the lender against the potential loss of capital."
                                            )}
                                        </p>
                                        <p className={styles.small}>
                                            {t("Let’s first discuss Property Security.")}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "Property security typically refers to a registered mortgage on real estate property in Australia. It serves as a statutory charge on the relevant land or interest, securing the amount of debt or liability, and is often supported by personal and directors’ guarantees."
                                            )}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "Additionally, a General Security Agreement (GSA) is established, covering the borrowing entity and other affiliated entities that indirectly or directly contribute to loan serviceability."
                                            )}
                                        </p>
                                    </section>
                                    <section className={styles.contentSection}>
                                        <h1 className={styles.big}>{t("Types of Property as Security")}</h1>
                                        <h2 className={styles.pointHeading}>{t("Residential Property")}</h2>
                                        <p className={styles.small}>
                                            {t(
                                                "Utilizing a residential property as collateral for a business loan often provides borrowers with access to more favourable terms."
                                            )}
                                        </p>
                                        <h2 className={styles.pointHeading}>{t("Commercial Property")}</h2>
                                        <p className={styles.small}>
                                            {t(
                                                "Lending with commercial property as collateral encompasses diverse options. Security types can be broadly classified as Standard or Non-Standard. Standard commercial properties are typically preferred and offer more favourable terms. They provide more lending options."
                                            )}
                                        </p>
                                        <h2 className={styles.pointHeading}>{t("Examples of Standard Commercial Property:")}</h2>
                                        <ol className={styles.pointsList}>
                                            <li>{t("Commercial Offices")}</li>
                                            <li>{t("Warehouses & Factories")}</li>
                                            <li>{t("Retail Premises")}</li>
                                        </ol>
                                        <h2 className={styles.pointHeading}>{t("Specialised Commercial Property")}</h2>
                                        <p className={styles.small}>
                                            {t(
                                                "Specialized commercial properties pose higher risks to credit providers due to their narrower market availability upon exit."
                                            )}
                                        </p>
                                        <h2 className={styles.pointHeading}>{t("Examples of Specialised Commercial Property:")}</h2>
                                        <ol className={styles.pointsList}>
                                            <li>{t("Accommodation (Motels, Hotels, Caravan Parks)")}</li>
                                            <li>{t("Farms")}</li>
                                            <li>{t("Child Care Facilities")}</li>
                                            <li>{t("Aged Care Facilities")}</li>
                                        </ol>
                                    </section>
                                    <section className={styles.contentSection}>
                                        <h1 className={styles.big}>{t("Conclusion")}</h1>
                                        <p className={styles.small}>
                                            {t(
                                                "In summary, property security is a vital aspect of lending and borrowing. It safeguards lenders from potential losses and offers borrowers access to favourable loan terms."
                                            )}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "Residential and standard commercial properties are preferred choices, while specialized commercial properties carry higher risks. Understanding property security empowers informed financial decisions, benefiting both borrowers and lenders in their pursuit of success."
                                            )}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "Our team will assess your financial position, industry experience, and secure the financing you need."
                                            )}
                                        </p>
                                        <div className={styles.meetingDiv}>
                                            <Link href="/booking" className={styles.ctaButton}>
                                                {t("Book a meeting now")}
                                            </Link>
                                        </div>
                                    </section>
                                    <Disclaimer />
                                </article>
                                <BlogSpreadLoveComponent url={blogData.url} title={blogData.title} />
                            </div>
                        </div>
                        <div className="col-12 col-md-4 col-lg-4">
                            <div className={styles.searchSection}>
                                <Suspense>
                                    <BlogSearchSection />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.BlogCardSliderWrapper}>
                    <div className="container">
                        <div className={styles.BlogCardSliderContent}>
                            <BlogCardSlider posts={relatedPosts} />
                        </div>
                    </div>
                </div>
                <div className={styles.leaveReplyWrapper}>
                    <div className="container">
                        <div className={styles.leaveReplyContent}>
                            <BlogLeaveReplyComponent />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}